import { useCallback, useEffect, useState } from "react";
import { X, History, RotateCcw, Plus, Loader2 } from "lucide-react";
import { api, apiError } from "../lib/api";

export const VersionHistoryDrawer = ({ open, projectId, onClose, onRestored, currentTitle }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/projects/${projectId}/versions`);
      setVersions(data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const saveVersion = async () => {
    setSaving(true);
    setError("");
    try {
      await api.post(`/projects/${projectId}/versions`, { label: label.trim() || null });
      setLabel("");
      await load();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const restore = async (versionId) => {
    if (!window.confirm("Restore this version? Your current state will be auto-snapshotted first.")) return;
    setRestoringId(versionId);
    setError("");
    try {
      const { data } = await api.post(`/projects/${projectId}/versions/${versionId}/restore`);
      onRestored?.(data);
      await load();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setRestoringId(null);
    }
  };

  if (!open) return null;

  const fmt = (ts) => new Date(ts).toLocaleString();

  return (
    <div className="mvp-drawer-backdrop" onClick={onClose}>
      <aside className="mvp-drawer" onClick={(e) => e.stopPropagation()} data-testid="version-history-drawer">
        <header className="mvp-drawer-head">
          <h3><History size={18} /> Version History</h3>
          <button className="mvp-icon-btn" onClick={onClose} aria-label="Close" data-testid="version-drawer-close">
            <X size={18} />
          </button>
        </header>
        <div className="mvp-drawer-body">
          <div className="mvp-version-save">
            <input
              type="text"
              placeholder="Label this snapshot (optional)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={120}
              data-testid="version-label-input"
            />
            <button
              className="btn-primary mvp-cta-sm"
              onClick={saveVersion}
              disabled={saving}
              data-testid="save-version-btn"
            >
              <Plus size={14} /> {saving ? "Saving…" : "Save Version"}
            </button>
          </div>
          <p className="mvp-dim mvp-drawer-hint">Keeps the most recent 20 versions per project.</p>
          {error && <div className="mvp-error" data-testid="version-error">{error}</div>}
          {loading ? (
            <div className="mvp-center"><Loader2 size={22} className="mvp-spin" /></div>
          ) : versions.length === 0 ? (
            <div className="mvp-version-empty">No versions yet. Autosaves create snapshots on significant edits.</div>
          ) : (
            <ul className="mvp-version-list" data-testid="version-list">
              {versions.map((v) => (
                <li key={v.id} className="mvp-version-item" data-testid={`version-item-${v.id}`}>
                  <div className="mvp-version-meta">
                    <span className="mvp-version-title">{v.title || currentTitle}</span>
                    <span className="mvp-version-label">{v.label || "Autosnapshot"}</span>
                    <span className="mvp-dim mvp-version-date">{fmt(v.created_at)}</span>
                  </div>
                  <button
                    className="mvp-nav-ghost"
                    onClick={() => restore(v.id)}
                    disabled={restoringId === v.id}
                    data-testid={`restore-version-${v.id}`}
                  >
                    <RotateCcw size={14} /> {restoringId === v.id ? "Restoring…" : "Restore"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
};
